import { modelCallState } from '@/api/pipeline'
import {
  JURISDICTIONS,
  JURISDICTION_OFFICES,
  type Jurisdiction,
  type MatterDetail,
  type PipelineRun,
} from '@/api/types'
import type { BadgeTone } from '@/components/ui'

/**
 * What an attorney should do about this matter, in a sentence, with one button.
 *
 * The Case screen was built for the person auditing the pipeline, and it is
 * good at that: every refusal the runner made is on the page, in the order it
 * made them. The reported problem is what that costs everybody else — *"the
 * flow is still stuck and its not very easy to understand flow, attorney using
 * first time will be lost in technical flow"*. A first-time reader met ten
 * cards of stage names, invariant numbers and flag vocabulary, and no sentence
 * anywhere saying what to do next.
 *
 * So this module answers one question the old screen never asked: **given this
 * run and this matter, what is the next thing a person does?** It is a pure
 * function over what the server already sent, kept apart from the rendering so
 * the answer can be asserted directly rather than inferred from markup.
 *
 * ## It decides nothing
 *
 * `exportable`, `verdict`, `blocks` and every finding severity are read off the
 * response exactly as the server decided them, for the reason the screen's own
 * docstring gives: a client with its own rule about what may be sent is a
 * second gate, and the second gate is always the lenient one. Nothing here
 * recomputes those. What it adds is *ordering and wording* — which of the
 * server's facts is the one to act on, and how to say it without a code in it.
 *
 * ## The stale block
 *
 * The case that prompted this: a run blocked because the matter had no
 * confirmed deadline, an attorney who then went and confirmed one, and a screen
 * that carried on showing the old refusal with no way forward. The facts had
 * moved and the run had not. {@link nextStep} detects exactly that — a run
 * blocked on the deadline whose matter now carries a *different, confirmed*
 * date — and says to run it again, which is the whole of what was missing.
 */

/** An id on the Case screen the next step can send somebody to. */
export const CASE_ANCHORS = {
  deadline: 'case-deadline',
  worklist: 'case-worklist',
  rerun: 'case-rerun',
  draft: 'case-draft',
} as const

export interface NextStepAction {
  label: string
  /** An element id on this screen to scroll to. */
  scrollTo?: string
  /** A router path, when the next step is on another screen. */
  navigateTo?: string
}

export type NextStepKind =
  | 'closed'
  | 'running'
  | 'abandoned'
  | 'failed'
  | 'ready'
  | 'deadline_resolved'
  | 'deadline_missing'
  | 'deadline_unsigned'
  | 'blocked'
  | 'declined'
  | 'held'
  | 'not_built'
  | 'dry_run'
  | 'no_draft'

export interface NextStep {
  kind: NextStepKind
  tone: BadgeTone
  /** Where the matter stands. One line, no codes. */
  headline: string
  /** Why, and what it means, in plain sentences. */
  detail: string
  /** The single thing to do now, or `null` when there is nothing to do. */
  action: NextStepAction | null
}

/** A date as an attorney writes it, never an ISO string in prose. */
function readableDate(iso: string | null): string {
  if (!iso) return 'no date'
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * A server error, short enough for a headline.
 *
 * The stored `error` is `TypeName: message`, capped, and a database error's
 * message runs on into the statement that failed. A reader needs the first
 * clause (`UndefinedColumn: column tm_cite.citation does not exist`) and the run
 * id to give an administrator; the rest is on the technical record below.
 */
export function shortError(error: string | null | undefined): string {
  const first = (error ?? '').split('\n')[0] ?? ''
  const cut = first.split(/\s\[SQL:|\sLINE \d+:/)[0]?.trim() ?? ''
  if (!cut) return 'no error was recorded'
  return cut.length > 160 ? `${cut.slice(0, 157)}…` : cut
}

function countBlocks(run: PipelineRun): number {
  return run.gate_findings.filter((finding) => finding.severity === 'BLOCK').length
}

/**
 * The office an attorney would name it by — "the UKIPO", not "GB".
 *
 * `run.office` is a bare string on the wire, so an unrecognised value falls
 * back to a phrase that still reads as a sentence rather than printing a code
 * nobody outside this codebase would recognise.
 */
/**
 * The four refusals, as a sentence rather than a constant.
 *
 * `NON_ENGLISH` is a fine name for the rule that raised it and a poor thing to
 * show somebody who has to decide what to do about it. Each of these says what
 * was wrong with the document *and* what would fix it, because "declined" with
 * no remedy is the shape that makes an honest refusal look like a dead end.
 */
const DECLINE_COPY: Record<string, string> = {
  NON_ENGLISH:
    'the office action is not in English. We do not draft from a machine translation nobody has checked — send the English text, or the official translation, and run it again',
  UNREADABLE_PDF:
    'the file could not be read as text. That is usually a scan without a text layer: run it through OCR, or paste the text in directly',
  WIPO_IRREGULARITY:
    'this is a WIPO irregularity notice, not an office action from a national registry. It is answered through WIPO on a different timetable, so drafting a response here would be answering the wrong body',
  OFFICE_AMBIGUOUS:
    'we could not tell which registry issued it. Deadlines and grounds differ by office, so guessing would put every date in the response at risk — confirm the office on the matter and run it again',
}

function officeName(office: string | null): string {
  return (JURISDICTIONS as readonly string[]).includes(office ?? '')
    ? `the ${JURISDICTION_OFFICES[office as Jurisdiction]}`
    : 'this office'
}

/**
 * Has the deadline moved on since this run looked at it?
 *
 * The comparison is against the run's own reconciliation rather than a
 * timestamp, because the question is not "was an event appended later" but "is
 * the matter now working to a date this run never saw" — which is the thing a
 * re-run would actually change. A confirmed date the run already had is not a
 * reason to run anything again.
 */
function deadlineMovedOn(run: PipelineRun, matter: MatterDetail | undefined): boolean {
  if (!matter?.deadline_verified || !matter.deadline_date) return false
  return matter.deadline_date !== (run.reconciliation?.deadline_date ?? null)
}

export function nextStep(
  run: PipelineRun | null | undefined,
  matter: MatterDetail | undefined,
): NextStep {
  if (matter && (matter.lifecycle === 'CLOSED' || matter.lifecycle === 'DECLINED')) {
    return {
      kind: 'closed',
      tone: 'neutral',
      headline: `This matter is ${matter.lifecycle.toLowerCase()}`,
      detail:
        'It was ended deliberately and nothing further will run on it. The record below stays readable — closing a matter files it, it does not delete it.',
      action: null,
    }
  }

  if (!run) {
    return {
      kind: 'no_draft',
      tone: 'neutral',
      headline: 'Nothing has been run on this matter yet',
      detail: `No work has been asked for. Paste the ${
        matter?.proceeding_type === 'OPPOSITION' ? 'notice of opposition' : 'office action'
      } below and run it to get a first read and a draft.`,
      action: { label: 'Run this matter', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  if (run.status === 'running' && !run.abandoned) {
    return {
      kind: 'running',
      tone: 'accent',
      headline: 'Working on this now',
      detail:
        'The pipeline is reading the document and drafting. This page updates itself as it goes — there is nothing to do until it finishes.',
      action: null,
    }
  }

  if (run.abandoned) {
    return {
      kind: 'abandoned',
      tone: 'warn',
      headline: 'This run never came back',
      detail:
        'The request behind it died part-way — a closed tab, a timeout, or a restart. Nothing was saved as a draft, so nothing was lost except the time. Run it again when you need this answered.',
      action: { label: 'Run this again', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  // The process did not vanish (that is `abandoned`) — it raised. The run row
  // says `failed` and carries the error, and the screen used to say only that
  // the pipeline "stopped before it reached the drafting stage", with the one
  // fact an administrator needs two cards further down (TM-16).
  if (run.status === 'failed') {
    return {
      kind: 'failed',
      tone: 'block',
      headline: 'This run stopped on a server error',
      detail: `Nothing was drafted. The error was: ${shortError(run.error)}. This is not something to fix in the document — tell an administrator, and quote run ${run.id.slice(0, 8)}. Running it again is safe, but it will fail the same way until the cause is fixed.`,
      action: { label: 'Run this again', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  if (run.verdict === 'declined') {
    return {
      kind: 'declined',
      tone: 'neutral',
      headline: 'We cannot work from this document',
      detail: `${
        (run.decline_reason && DECLINE_COPY[run.decline_reason]) ??
        'the document could not be used, and the pipeline stopped rather than guess at it'
      }. Stopping here is the system working, not failing — a response built on a document nobody could read is what this refusal exists to prevent.`
        // The reason is a sentence fragment, so the paragraph opens on it.
        .replace(/^./, (first) => first.toUpperCase()),
      action: { label: 'Try a clearer copy', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  if (run.verdict === 'held') {
    return {
      kind: 'held',
      tone: 'warn',
      headline: 'This one needs you to read it yourself',
      detail:
        'The reading came back ambiguous — not clear enough to draft from, and not wrong enough to refuse outright. Read the office action, correct anything that is wrong on this matter, and run it again.',
      action: { label: 'Run this again', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  // The runner ends a deliberate dry run and a genuinely missing port with the
  // same verdict, `not_built`; `modelCallState` is the one place that tells them
  // apart (`s5_generate` skipped because no generator was supplied). Reading the
  // verdict alone headlined every dry run — the New case wizard's default — as
  // "needs a step that is not built yet", which sent a tester looking for a bug.
  if (run.verdict === 'not_built' && modelCallState(run) === 'dry_run') {
    return {
      kind: 'dry_run',
      tone: 'neutral',
      headline: 'Dry run: no draft was requested',
      detail:
        'Everything short of the draft ran — the document was read, the deadline checked and the prompt prepared — and no model was called, as asked. Run it again with “Write the draft too” ticked when you want one.',
      action: { label: 'Run it with a draft', scrollTo: CASE_ANCHORS.rerun },
    }
  }

  if (run.verdict === 'not_built') {
    return {
      kind: 'not_built',
      tone: 'neutral',
      headline: 'This matter needs a step that is not built yet',
      detail:
        'Nothing went wrong. The pipeline reached a part of the work this build does not do and stopped instead of improvising. The technical record below names the missing piece.',
      action: null,
    }
  }

  // An exportable run whose own reconciliation still blocks is not ready, and
  // is not told it is: the server lets a draft through while the deadline is
  // open (drafting early is cheap; sending early is what cannot be undone), so
  // `exportable` alone said "every check passed" over a deadline the next
  // section of this very screen was calling a BLOCK. Those fall through to the
  // deadline steps below, which already say what to do about each.
  if (run.exportable && !run.reconciliation?.blocks) {
    const confirmed = Boolean(matter?.deadline_verified && matter.deadline_date)
    return {
      kind: 'ready',
      tone: 'ok',
      headline: 'Ready for your review',
      detail: confirmed
        ? 'The automatic checks passed and nothing is holding this back. Read the draft below, then take it to the Workbench to review, sign and export it.'
        : `The automatic checks passed. ${
            matter?.deadline_date
              ? `The deadline, ${readableDate(matter.deadline_date)}, has not been confirmed by a person yet`
              : 'No deadline is recorded on this matter yet'
          } — approving and signing in the Workbench is where that is confirmed, and it is checked again then. Read the draft below first.`,
      action: {
        label: 'Open in Workbench',
        navigateTo: `/workbench/${encodeURIComponent(run.matter_id)}`,
      },
    }
  }

  // Everything from here is a run that produced no sendable draft. The deadline
  // is checked before the gate because it is the one blocker a person clears on
  // this screen — and the one the old screen left with no way forward.
  if (run.reconciliation?.blocks) {
    if (deadlineMovedOn(run, matter)) {
      return {
        kind: 'deadline_resolved',
        tone: 'accent',
        // Deliberately says what the next run will *use*, not what it will
        // conclude. Whether a supplied date clears the block depends on the
        // step — an ordinary response stops blocking, a counterstatement
        // deadline with no rule behind it still does — and that policy lives on
        // the server. A screen that promised "this is now unblocked" would be
        // guessing at it, and would be wrong on exactly the matters where being
        // wrong costs the application.
        headline: 'The deadline is confirmed — run this again',
        detail: `This run stopped over the response deadline, and you have since confirmed ${readableDate(
          matter?.deadline_date ?? null,
        )}. A new run works from that date instead of the empty one this run had. If anything else is still outstanding, the new run will say so.`,
        action: { label: 'Run this again', scrollTo: CASE_ANCHORS.rerun },
      }
    }

    if (!matter?.deadline_date) {
      const office = officeName(run.office)
      return {
        kind: 'deadline_missing',
        tone: 'block',
        headline: 'Set the response deadline to continue',
        detail: `Nothing on the office action gave us a date, and ${office} has no rule we can work one out from. Take the deadline off the official letter and record it — nothing is drafted against a deadline nobody has confirmed, because filing late is the one mistake this cannot undo.`,
        action: { label: 'Set the deadline', scrollTo: CASE_ANCHORS.deadline },
      }
    }

    if (!matter.deadline_verified) {
      return {
        kind: 'deadline_unsigned',
        tone: 'block',
        headline: 'Confirm the deadline to continue',
        detail: `${readableDate(
          matter.deadline_date,
        )} is on this matter, but nobody has checked it against the papers yet. Confirm it, then run this again. A date agreeing with a rule is not the same as an attorney having read it.`,
        action: { label: 'Review the deadline', scrollTo: CASE_ANCHORS.deadline },
      }
    }

    return {
      kind: 'deadline_unsigned',
      tone: 'block',
      headline: 'The two deadline dates do not agree',
      detail: `The date printed on the papers and the date the rules work out are different, so the matter is not drafted against either until somebody says which is right. The technical record below shows both.`,
      action: { label: 'Review the deadline', scrollTo: CASE_ANCHORS.deadline },
    }
  }

  const blocks = countBlocks(run)
  if (blocks > 0) {
    return {
      kind: 'blocked',
      tone: 'block',
      headline:
        blocks === 1
          ? 'One thing to resolve before this can go out'
          : `${blocks} things to resolve before this can go out`,
      detail:
        'There is a draft and you can read it below — it is kept precisely so you can see what was written. The checks found problems with it, and it cannot be exported until those are cleared.',
      action: { label: 'See what is blocking', scrollTo: CASE_ANCHORS.worklist },
    }
  }

  return {
    kind: 'no_draft',
    tone: 'neutral',
    headline: 'No draft came out of this run',
    detail:
      'The run finished without producing anything to send. The technical record below says where it stopped — that is the thing to act on, not the missing draft. When the reason is fixed, run it again.',
    action: { label: 'Run this again', scrollTo: CASE_ANCHORS.rerun },
  }
}

// ---------------------------------------------------------------------------
// The rail
// ---------------------------------------------------------------------------

export type ProgressState = 'done' | 'blocked' | 'todo'

export interface ProgressStep {
  label: string
  state: ProgressState
  /** One short line: what this step means, for somebody who has not seen it. */
  hint: string
}

/**
 * The five things that have to happen, and how far this matter got.
 *
 * A first-time reader's real question is not "which stage stopped" but "how
 * much of this is done". The stage list answers the first and is kept, below,
 * for whoever wants it; this answers the second in the vocabulary of the work
 * rather than of the runner — no `s2_normalize`, no outcome enum.
 *
 * Each state is read off a value the server already decided. Nothing here is a
 * second opinion about whether a draft may be sent: the last step simply
 * reports `exportable`.
 */
export function progressSteps(
  run: PipelineRun | null | undefined,
  matter: MatterDetail | undefined,
): ProgressStep[] {
  const parsed = run?.stages.some(
    (stage) => stage.stage === 's1_parse' && stage.outcome === 'passed',
  )
  // A signature alone does not tick this step. An attorney can sign a date and
  // still have the run stopped on it — a printed date and a rule-computed date
  // that disagree are both "confirmed" and still irreconcilable — and a rail
  // showing the deadline done above a headline saying the two dates do not
  // agree is the screen contradicting itself. Signed *and* either not blocking
  // or blocking over a date the matter has since moved past is what counts.
  const signed = Boolean(matter?.deadline_verified && matter.deadline_date)
  const stillBlocking = Boolean(run?.reconciliation?.blocks)
  const deadlineOk =
    signed && (!stillBlocking || (run ? deadlineMovedOn(run, matter) : false))
  const deadlineBlocks = stillBlocking && !deadlineOk
  const hasDraft = Boolean(run?.draft)
  const blocks = run ? countBlocks(run) : 0
  const exportable = Boolean(run?.exportable)

  return [
    {
      label: 'Document read',
      state: parsed ? 'done' : run ? 'blocked' : 'todo',
      hint: 'The office action was legible and we got the text out of it.',
    },
    {
      label: 'Deadline confirmed',
      state: deadlineOk ? 'done' : deadlineBlocks ? 'blocked' : 'todo',
      hint: 'An attorney has checked the response date against the papers.',
    },
    {
      label: 'Draft written',
      state: hasDraft ? 'done' : 'todo',
      hint: 'The response has been drafted against the grounds raised.',
    },
    {
      label: 'Checks passed',
      state: hasDraft && blocks === 0 ? 'done' : blocks > 0 ? 'blocked' : 'todo',
      hint: 'Nothing cited is invented, and nothing raised is left unanswered.',
    },
    {
      label: 'Ready to send',
      // The server's `exportable` AND a deadline a person has confirmed. The row
      // used to read "done" beside "Deadline confirmed: not reached" (TM-08) —
      // the rail contradicting itself. Not a second gate: it adds no refusal, it
      // only declines to tick a box over an open one.
      state: exportable && deadlineOk ? 'done' : 'todo',
      hint: 'Exportable, and its deadline confirmed by a person.',
    },
  ]
}
